// components/Common/RichText.jsx
// ──────────────────────────────────────────────────────────────────────────────
// Renders rich-text content produced by the ReactQuill editor (course
// description, syllabus, prerequisites, highlights, etc.) in a consistent,
// structured way across the app:
//   • HTML content  → sanitized with DOMPurify and styled with Quill's
//     `.ql-editor` rules so headings / lists / colors match the editor.
//   • Plain text     → rendered as-is, preserving line breaks (legacy data
//     created before the rich editor existed).
//   • Empty          → renders nothing (including editor "empty" markup such as
//     `<p><br></p>`, which Quill emits for a blank field).
//
// NEVER interpolate these fields directly into JSX — React escapes the markup
// and the user sees literal `<p>` tags. Use <RichText/> for full content and
// `richTextSnippet()` / `stripHtml()` for card previews, meta descriptions and
// anything else that needs a single line of plain text.
//
// Those text helpers live in `utils/richText.js` — importable anywhere without
// dragging in Quill's stylesheet — and are re-exported here for convenience.
// ──────────────────────────────────────────────────────────────────────────────

import React from 'react';
import DOMPurify from 'dompurify';
import { isHtml, hasRichContent } from '../../utils/richText';
import 'react-quill-new/dist/quill.snow.css';
import './RichText.scss';

export { isHtml, stripHtml, hasRichContent, richTextSnippet } from '../../utils/richText';

/**
 * @param compact  Use the tighter UI type scale instead of the default editorial
 *                 one. For prose inside a card, panel or tab, where the default
 *                 sizes (a stored `<h1>` at 1.375rem) overpower the surrounding
 *                 interface. Opt-in, so existing callers are unaffected.
 */
export default function RichText({ value, className = '', compact = false }) {
  if (!hasRichContent(value)) return null;

  const base = `rt-content${compact ? ' rt-content--compact' : ''}`;

  if (isHtml(value)) {
    return (
      <div
        className={`ql-editor ${base} ${className}`}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
      />
    );
  }

  // Legacy plain text — keep line breaks.
  return (
    <div className={`${base} rt-content--plain ${className}`} style={{ whiteSpace: 'pre-line' }}>
      {value}
    </div>
  );
}
